import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/api";
import { UserPlus, ArrowLeft } from "lucide-react";

const Register = () => {
  const { t } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await register({ email, password, name, phone });
      navigate("/my");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Registrierung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rs-app min-h-screen bg-white">
      <Header />
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 min-h-[calc(100vh-5rem)]">
        <aside className="md:col-span-5 lg:col-span-4 bg-[var(--rs-primary)] text-white p-10 lg:p-14 flex flex-col justify-between">
          <div>
            <div className="rs-overline !text-white/60">NIVARA · KUNDE</div>
            <h1 className="font-[Chivo] text-5xl font-black mt-3 leading-[0.95]">
              Direkt
              <br />
              <span className="text-[var(--rs-accent)]">buchen.</span>
            </h1>
            <p className="text-white/60 mt-6 max-w-sm">
              Erstellen Sie Ihr Kundenkonto und verwalten Sie Buchungen und Verträge online.
            </p>
          </div>
          <div className="font-[Chivo] tracking-[0.4em] text-white/30 text-sm mt-12">NIVARAGROUP.CH</div>
        </aside>
        <div className="md:col-span-7 lg:col-span-8 p-8 md:p-14 flex items-center">
          <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
            <Link to="/" className="text-xs uppercase tracking-[0.2em] text-[var(--rs-text-muted)] hover:text-[var(--rs-text)] inline-flex items-center gap-2">
              <ArrowLeft size={12} /> Zurück
            </Link>
            <h2 className="font-[Chivo] text-3xl font-black mt-4">{t("register_title")}</h2>
            <p className="text-[var(--rs-text-muted)] mt-2">{t("register_sub")}</p>

            <div className="mt-8 space-y-5">
              <label className="block">
                <span className="rs-label">{t("f_full_name")}</span>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="rs-input" required data-testid="register-name" />
              </label>
              <label className="block">
                <span className="rs-label">{t("f_email_login")}</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rs-input" required autoComplete="email" data-testid="register-email" />
              </label>
              <label className="block">
                <span className="rs-label">{t("f_phone")}</span>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="rs-input" data-testid="register-phone" />
              </label>
              <label className="block">
                <span className="rs-label">{t("f_password")} (min. 6)</span>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rs-input" required minLength={6} autoComplete="new-password" data-testid="register-password" />
              </label>
            </div>

            {err && (
              <div className="mt-4 border-l-4 border-[var(--rs-primary)] bg-[var(--rs-primary)]/5 px-4 py-3 text-sm" data-testid="register-error">
                {err}
              </div>
            )}

            <button type="submit" className="rs-btn-primary mt-8 w-full" disabled={busy} data-testid="register-submit">
              <UserPlus size={18} className="mr-2" />
              {busy ? "…" : t("register_cta")}
            </button>
            <div className="mt-6 text-sm text-[var(--rs-text-muted)]">
              {t("have_account")} <Link to="/login" className="font-bold underline">{t("nav_login")}</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
